/**
 * MinaRoMina Search Modal Functionality
 *
 * Handles predictive search with debounce, live results,
 * and keyboard navigation for the search modal.
 */

class MinaRominaSearch {
  constructor(modal) {
    this.modal = modal;
    this.input = modal.querySelector('[data-search-input]');
    this.resultsContainer = modal.querySelector('[data-results-container]');
    this.loadingElement = modal.querySelector('[data-loading]');
    this.emptyElement = modal.querySelector('[data-empty]');
    this.suggestionsElement = modal.querySelector('[data-suggestions]');
    this.viewAllElement = modal.querySelector('[data-view-all]');
    this.viewAllLink = modal.querySelector('[data-view-all-link]');

    // Result sections
    this.productsSection = modal.querySelector('[data-products]');
    this.collectionsSection = modal.querySelector('[data-collections]');
    this.pagesSection = modal.querySelector('[data-pages]');

    // Result lists
    this.productsList = modal.querySelector('[data-product-list]');
    this.collectionsList = modal.querySelector('[data-collection-list]');
    this.pagesList = modal.querySelector('[data-page-list]');

    this.searchTimeout = null;
    this.currentQuery = '';
    this.debounceTime = 300;

    this.init();
  }

  init() {
    if (!this.input) return;

    // Input event listeners
    this.input.addEventListener('input', this.handleInput.bind(this));
    this.input.addEventListener('keydown', this.handleKeydown.bind(this));
    this.input.addEventListener('focus', this.handleFocus.bind(this));

    // Form submission
    const form = this.modal.querySelector('.mm-search-modal__form');
    if (form) {
      form.addEventListener('submit', this.handleSubmit.bind(this));
    }
  }

  handleInput(e) {
    const query = e.target.value.trim();

    // Clear previous timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    // Debounce search
    this.searchTimeout = setTimeout(() => {
      this.performSearch(query);
    }, this.debounceTime);
  }

  handleKeydown(e) {
    // Handle keyboard navigation
    switch (e.key) {
      case 'Escape':
        this.closeSearch();
        break;
      case 'ArrowDown':
        e.preventDefault();
        this.navigateResults('down');
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.navigateResults('up');
        break;
      case 'Enter':
        e.preventDefault();
        this.selectCurrentResult();
        break;
    }
  }

  handleFocus() {
    // Show suggestions if input is empty
    if (!this.input.value.trim()) {
      this.showSuggestions();
    }
  }

  handleSubmit(e) {
    e.preventDefault();
    const query = this.input.value.trim();
    if (query) {
      window.location.href = `/search?q=${encodeURIComponent(query)}`;
    }
  }

  async performSearch(query) {
    this.currentQuery = query;

    if (!query) {
      this.showSuggestions();
      return;
    }

    // Show loading state
    this.showLoading();

    try {
      const searchUrl = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection,page&resources[limit]=6&resources[options][unavailable_products]=last`;

      const response = await fetch(searchUrl);

      if (!response.ok) {
        throw new Error('Search request failed');
      }

      const data = await response.json();

      // Only show results if this is still the current query
      if (query === this.currentQuery) {
        this.displayResults(data, query);
      }

    } catch (error) {
      console.error('Search error:', error);
      this.showEmpty();
    }
  }

  showLoading() {
    this.hideAllSections();
    this.loadingElement.hidden = false;
  }

  showEmpty() {
    this.hideAllSections();
    this.emptyElement.hidden = false;
  }

  showSuggestions() {
    this.hideAllSections();
    if (this.suggestionsElement) {
      this.suggestionsElement.hidden = false;
    }
  }

  hideAllSections() {
    const sections = [
      this.loadingElement,
      this.emptyElement,
      this.suggestionsElement,
      this.productsSection,
      this.collectionsSection,
      this.pagesSection,
      this.viewAllElement
    ];

    sections.forEach(section => {
      if (section) section.hidden = true;
    });
  }

  displayResults(data, query) {
    this.hideAllSections();

    const hasProducts = data.resources?.results?.products?.length > 0;
    const hasPages = data.resources?.results?.pages?.length > 0;

    if (!hasProducts && !hasPages) {
      this.showEmpty();
      return;
    }

    // Display products
    if (hasProducts) {
      this.displayProducts(data.resources.results.products);
      this.productsSection.hidden = false;
    }

    // Display pages
    if (hasPages) {
      this.displayPages(data.resources.results.pages);
      this.pagesSection.hidden = false;
    }

    // Show "View All" link
    if (this.viewAllLink) {
      this.viewAllLink.href = `/search?q=${encodeURIComponent(query)}`;
      this.viewAllElement.hidden = false;
    }
  }

  displayProducts(products) {
    if (!this.productsList) return;

    const productHTML = products.map(product => {
      const price = product.price ? this.formatMoney(product.price) : '';
      // Use compare_at_price_max or compare_at_price for discount
      const compareAtPrice = product.compare_at_price_max || product.compare_at_price;
      const comparePrice = compareAtPrice && compareAtPrice > product.price
        ? this.formatMoney(compareAtPrice)
        : '';

      return `
        <a href="${product.url}" class="mm-search-modal__result-item">
          <div class="mm-search-modal__result-image">
            ${product.featured_image ?
              `<img src="${product.featured_image.url}&width=80" alt="${product.featured_image.alt || product.title}" loading="lazy">` :
              `<div class="mm-search-modal__result-placeholder"></div>`
            }
          </div>
          <div class="mm-search-modal__result-content">
            <h4 class="mm-search-modal__result-title">${product.title}</h4>
            <div class="mm-search-modal__result-price">
              ${comparePrice ? `<span class="mm-search-modal__result-compare-price">${comparePrice}</span>` : ''}
              <span class="mm-search-modal__result-current-price">${price}</span>
            </div>
          </div>
        </a>
      `;
    }).join('');

    this.productsList.innerHTML = productHTML;
  }

  displayCollections(collections) {
    if (!this.collectionsList) return;

    const collectionHTML = collections.map(collection => {
      return `
        <a href="${collection.url}" class="mm-search-modal__result-item">
          <div class="mm-search-modal__result-image">
            ${collection.featured_image ?
              `<img src="${collection.featured_image.url}&width=80" alt="${collection.featured_image.alt || collection.title}" loading="lazy">` :
              `<div class="mm-search-modal__result-placeholder"></div>`
            }
          </div>
          <div class="mm-search-modal__result-content">
            <h4 class="mm-search-modal__result-title">${collection.title}</h4>
            <p class="mm-search-modal__result-type">Collection</p>
          </div>
        </a>
      `;
    }).join('');

    this.collectionsList.innerHTML = collectionHTML;
  }

  displayPages(pages) {
    if (!this.pagesList) return;

    const pageHTML = pages.map(page => {
      return `
        <a href="${page.url}" class="mm-search-modal__result-item">
          <div class="mm-search-modal__result-content mm-search-modal__result-content--full">
            <h4 class="mm-search-modal__result-title">${page.title}</h4>
            <p class="mm-search-modal__result-type">Page</p>
          </div>
        </a>
      `;
    }).join('');

    this.pagesList.innerHTML = pageHTML;
  }

  formatMoney(price) {
    // Detect currency dynamically based on Shopify settings or domain
    let currency = 'MXN';
    let locale = 'es-MX';

    // Try to get currency from Shopify global object
    if (typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.active) {
      currency = Shopify.currency.active;
    }
    // Fallback: detect by domain
    else if (window.location.hostname.includes('.us')) {
      currency = 'USD';
      locale = 'en-US';
    }

    return `$${parseFloat(price).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
  }

  navigateResults(direction) {
    // Keyboard navigation implementation
    const results = this.modal.querySelectorAll('.mm-search-modal__result-item:not([hidden])');
    if (results.length === 0) return;

    const currentFocus = this.modal.querySelector('.mm-search-modal__result-item:focus');
    let nextIndex = 0;

    if (currentFocus) {
      const currentIndex = Array.from(results).indexOf(currentFocus);
      if (direction === 'down') {
        nextIndex = (currentIndex + 1) % results.length;
      } else {
        nextIndex = currentIndex > 0 ? currentIndex - 1 : results.length - 1;
      }
    }

    results[nextIndex].focus();
  }

  selectCurrentResult() {
    const focused = this.modal.querySelector('.mm-search-modal__result-item:focus');
    if (focused) {
      focused.click();
    } else if (this.input.value.trim()) {
      // Submit search if no result is focused
      this.handleSubmit({ preventDefault: () => {} });
    }
  }

  closeSearch() {
    const closeButton = this.modal.querySelector('[data-close-search]');
    if (closeButton) {
      closeButton.click();
    }
  }
}

// Initialize search modals when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  const searchModals = document.querySelectorAll('.mm-search-modal');

  searchModals.forEach(modal => {
    new MinaRominaSearch(modal);

    // Modal open/close functionality
    const closeButtons = modal.querySelectorAll('[data-close-search]');
    const searchInput = modal.querySelector('[data-search-input]');

    // Close handlers
    closeButtons.forEach(button => {
      button.addEventListener('click', () => {
        modal.style.display = 'none';
        modal.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
      });
    });

    // ESC key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.getAttribute('aria-hidden') === 'false') {
        modal.style.display = 'none';
        modal.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
      }
    });
  });

  // Global search trigger (for header search button)
  const searchTriggers = document.querySelectorAll('[data-open-search]');
  searchTriggers.forEach(trigger => {
    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      const searchModal = document.querySelector('.mm-search-modal');
      if (searchModal) {
        // CRITICAL FIX: Force overlay-group to be visible first
        const overlayGroup = document.querySelector('.shopify-section-group-overlay-group');
        if (overlayGroup) {
          overlayGroup.style.cssText = 'display: block !important; position: fixed !important; inset: 0 !important; z-index: 10000 !important; pointer-events: none !important;';
          console.log('🔧 [Search] Overlay group forced visible');
        }

        // CRITICAL FIX: Force the shopify-section parent to be visible
        const shopifySection = searchModal.closest('.shopify-section');
        if (shopifySection) {
          shopifySection.style.cssText = 'display: block !important; position: fixed !important; inset: 0 !important; z-index: 9999 !important; pointer-events: none !important;';
          console.log('🔧 [Search] Shopify section forced visible');
        }

        // Force search modal dimensions
        searchModal.style.cssText = 'display: flex !important; position: fixed !important; inset: 0 !important; width: 100vw !important; height: 100vh !important; z-index: 9999 !important; pointer-events: auto !important; align-items: flex-start !important; justify-content: center !important; overflow-y: auto !important;';
        searchModal.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';

        // Force container visibility
        const container = searchModal.querySelector('.mm-search-modal__container');
        if (container) {
          container.style.cssText = 'display: block !important; width: 100% !important; max-width: 800px !important; min-height: 100vh !important; background-color: #fff !important; position: relative !important; z-index: 1 !important;';
          console.log('🔧 [Search] Container forced visible');
        }

        // Focus on search input
        const searchInput = searchModal.querySelector('[data-search-input]');
        if (searchInput) {
          setTimeout(() => searchInput.focus(), 100);
        }
      }
    });
  });
});